import { AI_AGENT_DEFAULT_NAME } from '@numeo/types-main-web-app'
import { ClientApplication, Company, isPublicEmailDomain, isValidEmailAddress, McVerificationStatus, PayrollPeriod } from '@numeo/types-core'

export interface CompanyFormData {
    companyName: string
    mcNumber: string
    dotNumber: string
    phoneNumber: string
    transferNumber: string
    aiAgentName: string
    additionalNotes: string
    address1: string
    address2: string
    city: string
    state: string
    zipCode: string
    dba?: string
    fein?: string
    companyEmail?: string
    payrollPeriod?: PayrollPeriod | ''
    logoUrl?: string
    // Comma-separated while editing; application-level like logoUrl, never per-email.
    domains?: string
    // Exact addresses exempted from `domains`, same comma-separated editing shape.
    domainExceptions?: string
}

/** Split a comma-separated filtering input (domains or exceptions) into the array the API expects.
 *  The server owns normalization and validation — this only has to stop empty entries from
 *  trailing commas. */
export const splitCompanyDomainsInput = (raw: string): string[] =>
    raw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)

/** Mirror of the normalization in the server's `validateCompanyDomains`. */
const normalizeDomainEntry = (entry: string): string =>
    entry
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^@/, '')
        .replace(/\/.*$/, '')

/**
 * A filtered-sender entry: a bare domain (acme.com) or a full address (ops@acme.com).
 *
 * Mirrors `validateCompanyDomains` in company.controller.ts exactly, public-provider rule included —
 * a bare `gmail.com` would swallow every broker hosted there. Diverging in either direction is a
 * bug: looser and a chip reads as accepted then 400s the whole save, stricter and the field refuses
 * an entry the endpoint would have taken.
 */
export const isCompanyDomainEntry = (entry: string): boolean => {
    const normalized = normalizeDomainEntry(entry)
    const isExactAddress = normalized.includes('@')
    const domain = isExactAddress ? normalized.split('@')[1] : normalized
    if (!domain || !domain.includes('.')) return false
    return isExactAddress || !isPublicEmailDomain(domain)
}

/**
 * An exception entry: always a full address, never a domain — they are matched exactly. Public
 * providers are fine here; an exact address only ever exempts one mailbox.
 *
 * Mirrors `validateCompanyDomainExceptions`, which runs the same shared validator.
 */
export const isCompanyExceptionEntry = (entry: string): boolean =>
    isValidEmailAddress(
        entry
            .trim()
            .toLowerCase()
            .replace(/^mailto:/, '')
    )

// Helper to extract form data from email's companyDetail (Multi-MC Phase 1 / pre-flag path).
// Used when `organization.companyFromParentEnabled !== true` — we still source company info per-email
// from `user.nylasEmails[].companyDetail`.
export const getFormDataFromCompanyDetail = (
    companyDetail:
        | {
              name?: string
              mcNumber?: string
              dotNumber?: string
              phoneNumber?: string
              transferNumber?: string
              aiAgentName?: string
              details?: string
              companyAddress?: string
          }
        | undefined
): CompanyFormData | null => {
    if (!companyDetail) return null

    const parts = companyDetail.companyAddress?.split(', ') || []
    const zipCode = parts[parts.length - 1] || ''
    const state = parts[parts.length - 2] || ''
    const city = parts[parts.length - 3] || ''
    const address1 = parts[0] || ''
    const address2 = parts.length >= 5 ? parts.slice(1, parts.length - 3).join(', ') : ''
    return {
        companyName: companyDetail.name || '',
        mcNumber: companyDetail.mcNumber || '',
        dotNumber: companyDetail.dotNumber || '',
        phoneNumber: companyDetail.phoneNumber || '',
        transferNumber: companyDetail.transferNumber || '',
        aiAgentName: companyDetail.aiAgentName || AI_AGENT_DEFAULT_NAME,
        additionalNotes: companyDetail.details || '',
        address1,
        address2,
        city,
        state,
        zipCode,
    }
}

/**
 * Fill empty fields of `primary` from `fallback`. Display priority for Multi-MC (flag-ON)
 * accounts: `application.settings.company` first, then the email's legacy per-email
 * companyDetail for anything the canonical source doesn't have yet (e.g. orgs migrated
 * before the company info was ever saved at the application level).
 */
// The postal-address fields form ONE logical value and must never be spliced from two sources —
// e.g. a street saved on settings.company combined with the city/state/zip of a DIFFERENT address
// parsed from the email's companyDetail would display (and be written back on the next save) as a
// corrupted address. They are merged as an atomic group below.
const ADDRESS_KEYS: (keyof CompanyFormData)[] = ['address1', 'address2', 'city', 'state', 'zipCode']

export const mergeCompanyFormData = (primary: CompanyFormData, fallback: CompanyFormData | null): CompanyFormData => {
    if (!fallback) return primary
    const merged: CompanyFormData = { ...primary }
    // If the primary has ANY address field, its address group wins wholesale (even the blanks);
    // the fallback's address is only used when the primary has no address at all.
    const primaryHasAddress = ADDRESS_KEYS.some((key) => primary[key])
    for (const key of Object.keys(fallback) as (keyof CompanyFormData)[]) {
        if (primaryHasAddress && ADDRESS_KEYS.includes(key)) continue
        const primaryValue = merged[key]
        const fallbackValue = fallback[key]
        if ((primaryValue === '' || primaryValue === undefined) && fallbackValue) {
            ;(merged as Record<keyof CompanyFormData, CompanyFormData[keyof CompanyFormData]>)[key] = fallbackValue
        }
    }
    return merged
}

// A company whose MC is verified pins its whole identity (name, MC, DOT, FEIN) to
// `settings.company` — that's the canonical record the verification wrote and the header badge reads.
// Such an account MUST read the form from `settings.company` even in per-email mode; otherwise the
// form shows the (empty/stale) per-email `companyDetail` while the badge shows verified, and a save
// re-posts that blank identity and trips the server-side identity lock (403). Mirrors the validity
// check the McVerification section uses: the badge only counts while the verified MC still matches
// the saved MC.
/** Minimal company shape needed to decide MC-verified — satisfied by `Company` and by the sidebar
 *  switcher's CompanyLike, so all three call sites share ONE predicate instead of diverging copies. */
type McVerifiableCompany = { mcNumber?: string; mcVerification?: { status?: string; verifiedMcNumber?: string } }

/**
 * Shared "is this company's MC verified" predicate for the header badge / sidebar switcher. Kept
 * EXACTLY in sync with `McVerificationSection`'s seedView (`v.verifiedMcNumber === company.mcNumber.trim()`)
 * so the settings badge and the switcher dot never disagree: status VERIFIED AND a saved MC AND the
 * verified MC trim-exactly matches it (an absent verifiedMcNumber counts as NOT verified, same as the
 * section). This is only used for that verified-badge display now — the GeneralTab form sources its
 * data via `shouldSourceCompanyFromSettings`, not this predicate.
 */
export const isCompanyRecordMcVerified = (company: McVerifiableCompany | null | undefined): boolean => {
    if (!company) return false
    if (company.mcVerification?.status !== McVerificationStatus.VERIFIED) return false
    const mc = company.mcNumber?.trim() || null
    return company.mcVerification.verifiedMcNumber === mc
}

export const isCompanyMcVerified = (application: Pick<ClientApplication, 'settings'> | null | undefined): boolean =>
    isCompanyRecordMcVerified(application?.settings?.company)

/**
 * Whether the GeneralTab form should read the company identity from the canonical `settings.company`
 * instead of the SELECTED email's `companyDetail`, in per-email mode (`companyFromParentEnabled` off).
 *
 * Deliberately NARROW so it never changes behavior for a normal per-email account: it returns true
 * ONLY when the selected email's `companyDetail` is BLANK. A populated per-email detail always wins —
 * we never replace a user's own per-email company data with `settings.company`. The override applies
 * only to the broken case (a detail that was never filled — e.g. an account verified before the
 * parent-company model), and only for an unambiguously single-company account (≤1 distinct MC across
 * settings.company and every connected email), so a multi-MC email is never filled from the anchor.
 *
 * This also intentionally does NOT gate on MC-verified status: that status is unreliable here because
 * the McVerification section overwrites `settings.company.mcVerification` in Redux with the freshly
 * DERIVED /status (which can be pending-review for a legacy email-verified account).
 */
export const shouldSourceCompanyFromSettings = (
    application: Pick<ClientApplication, 'settings'> | null | undefined,
    selectedCompanyDetail?: { name?: string; mcNumber?: string } | null,
    nylasEmails?: Array<{ companyDetail?: { mcNumber?: string } }> | null
): boolean => {
    const company = application?.settings?.company
    if (!company) return false
    const hasIdentity = Boolean((company.mcNumber || '').trim() || (company.name || '').trim())
    if (!hasIdentity) return false
    // A populated per-email detail always wins — only override a BLANK detail.
    const detailHasIdentity = Boolean((selectedCompanyDetail?.mcNumber || '').trim() || (selectedCompanyDetail?.name || '').trim())
    if (detailHasIdentity) return false
    const digits = (mc?: string) => (mc || '').replace(/\D/g, '').trim()
    const mcs = new Set<string>()
    const settingsMc = digits(company.mcNumber)
    if (settingsMc) mcs.add(settingsMc)
    for (const email of nylasEmails || []) {
        const m = digits(email?.companyDetail?.mcNumber)
        if (m) mcs.add(m)
    }
    return mcs.size <= 1
}

// Helper to extract form data from `application.settings.company` (Multi-MC Phase 2 / flag-ON path).
// Used when `organization.companyFromParentEnabled === true` — company info lives on the application,
// not per-email. Empty `settings.company` returns empty defaults so the user can fill and save.
export const getFormDataFromApplication = (application: Pick<ClientApplication, 'settings'> | null | undefined): CompanyFormData => {
    const company: Company | undefined = application?.settings?.company

    // No company yet — return empty defaults (user fills and saves).
    if (!company) {
        return {
            companyName: '',
            mcNumber: '',
            dotNumber: '',
            dba: '',
            fein: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipCode: '',
            companyEmail: '',
            phoneNumber: '',
            transferNumber: '',
            aiAgentName: AI_AGENT_DEFAULT_NAME,
            additionalNotes: '',
            payrollPeriod: '',
            logoUrl: '',
            domains: '',
            domainExceptions: '',
        }
    }

    // Prefer the structured `address1/2/city/state/zipCode` fields; fall back to splitting the legacy
    // `companyAddress` string when the structured fields are absent (older docs predate Phase 2).
    let address1 = company.address1 ?? ''
    let address2 = company.address2 ?? ''
    let city = company.city ?? ''
    let state = company.state ?? ''
    let zipCode = company.zipCode ?? ''

    const hasStructuredAddress = Boolean(address1 || address2 || city || state || zipCode)
    if (!hasStructuredAddress && company.companyAddress) {
        const parts = company.companyAddress.split(', ')
        zipCode = parts[parts.length - 1] || ''
        state = parts[parts.length - 2] || ''
        city = parts[parts.length - 3] || ''
        address1 = parts[0] || ''
        address2 = parts.length >= 5 ? parts.slice(1, parts.length - 3).join(', ') : ''
    }

    return {
        companyName: company.name || '',
        mcNumber: company.mcNumber || '',
        dotNumber: company.dotNumber || '',
        dba: company.dba || '',
        fein: company.fein || '',
        address1,
        address2,
        city,
        state,
        zipCode,
        companyEmail: company.email || '',
        phoneNumber: company.phoneNumber || '',
        transferNumber: company.transferNumber || '',
        aiAgentName: company.aiAgentName || AI_AGENT_DEFAULT_NAME,
        additionalNotes: company.details || '',
        payrollPeriod: company.payrollPeriod || '',
        logoUrl: company.logoUrl || '',
        domains: (company.domains || []).join(', '),
        domainExceptions: (company.domainExceptions || []).join(', '),
    }
}
